import * as React from 'react';
import initialize, { ApiError } from '@showell-labs/api-client';
import type { ShowellApi } from '@showell-labs/api-client';
import { SHW_ENV } from '@showell-labs/env';
import { authReducer } from './authReducer';
import { resolveLoginSession } from './resolveLoginSession';
import {
  clearStoredSession,
  getStoredSession,
  patchStoredSession,
  storeSession,
} from './storage';
import type { WorkspaceInfo } from './types';

interface AuthContextValue {
  isAuthenticated: boolean;
  api: ShowellApi | null;
  accountName: string | null;
  accountId: string | null;
  userName: string | null;
  workspaceDisplayName: string | null;
  applications: WorkspaceInfo[] | null;
  login: (
    accessToken: string,
    accountName: string | null | undefined,
    applications: WorkspaceInfo[],
    userName: string | null | undefined
  ) => void;
  /**
   * Called by WorkspaceResolverContext when the correct workspace for the
   * currently open document is determined. Configures the main API instance
   * with the workspace's accountId and persists the selection.
   */
  switchWorkspace: (
    accountName: string,
    accountId: string,
    displayName: string
  ) => void;
  logout: () => void;
}

const AuthContext = React.createContext<AuthContextValue>({
  isAuthenticated: false,
  api: null,
  accountName: null,
  accountId: null,
  userName: null,
  workspaceDisplayName: null,
  applications: null,
  login: () => {},
  switchWorkspace: () => {},
  logout: () => {},
});

const LOGOUT_APP_SESSION_KEY = 'powerpoint-add-in-logout-app-session';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(authReducer, undefined, () => ({
    session: getStoredSession(),
  }));

  // logout()-via-ref so handleApiError (captured by `initialize()` below)
  // can reach the real logout() defined later in this component.
  const logoutRef = React.useRef<() => void>(() => {});

  // Reactive token-expiry handling: when the server returns 401, the access
  // token is no longer valid (expired, revoked, etc.) → drop the session
  // and force re-login. We don't store an expiry timestamp locally, so this
  // is the only signal we have.
  const handleApiError = React.useCallback((event: { error: ApiError }) => {
    if (event.error.response?.status === 401) {
      logoutRef.current();
    }
  }, []);

  // Cold start: restore API with both token and accountId so all requests
  // immediately target the previously selected workspace.
  const [api, setApi] = React.useState<ShowellApi | null>(() => {
    const initial = getStoredSession();
    if (!initial) {
      return null;
    }
    return initialize({
      baseUrl: SHW_ENV.SHW_API_URL,
      accessToken: initial.accessToken,
      accountId: initial.accountId ?? undefined,
      onApiError: handleApiError,
    });
  });

  const login = React.useCallback(
    (
      accessToken: string,
      name: string | null | undefined,
      apps: WorkspaceInfo[],
      userDisplayName: string | null | undefined
    ) => {
      const newSession = resolveLoginSession(
        accessToken,
        name,
        apps,
        userDisplayName
      );

      storeSession(newSession);

      const apiInstance = initialize({
        baseUrl: SHW_ENV.SHW_API_URL,
        accessToken,
        accountId: newSession.accountId ?? undefined,
        onApiError: handleApiError,
      });

      setApi(apiInstance);
      dispatch({ type: 'setSession', session: newSession });
    },
    [handleApiError]
  );

  const switchWorkspace = React.useCallback(
    (workspace: string, workspaceAccountId: string, displayName: string) => {
      patchStoredSession({
        accountName: workspace,
        accountId: workspaceAccountId,
        workspaceDisplayName: displayName,
      });
      dispatch({
        type: 'switchWorkspace',
        accountName: workspace,
        accountId: workspaceAccountId,
        displayName,
      });
      // useAccount() with an entity ID calls configure({ accountId }) without
      // a network request — see ShowellApi.ts.
      if (api) {
        api.useAccount(workspaceAccountId);
      }
    },
    [api]
  );

  const logout = React.useCallback(() => {
    try {
      window.sessionStorage.setItem(LOGOUT_APP_SESSION_KEY, '1');
    } catch {
      // ignore session storage failures
    }
    clearStoredSession();
    setApi(null);
    dispatch({ type: 'clearSession' });
  }, []);

  // Wire up the logout-ref bridge so handleApiError can reach the latest
  // logout() implementation regardless of when it's called.
  React.useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  const value = React.useMemo(
    () => ({
      isAuthenticated: api !== null,
      api,
      accountName: state.session?.accountName ?? null,
      accountId: state.session?.accountId ?? null,
      userName: state.session?.userName ?? null,
      workspaceDisplayName: state.session?.workspaceDisplayName ?? null,
      applications: state.session?.applications ?? null,
      login,
      switchWorkspace,
      logout,
    }),
    [api, state.session, login, switchWorkspace, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return React.useContext(AuthContext);
}
